import { Suspense, lazy, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type {
  FileComparisonResponse,
  FileComparisonSide,
  WorkItemFile,
  WorkItemFilesResponse,
} from '@localagent/sdk';
import { cn } from '@localagent/ui/lib/utils';
import { errorWithNothingCachedToShow } from '../../lib/query-error';
import { getSDK } from '../../services/sdk-instance';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

/**
 * The changed-file list for one work item, and the read-only comparison a
 * selected row opens (cloud_filesystem ADR-029, cloud_filesystem ADR-031).
 */

// The comparison surface carries the whole editor chunk, so it loads only when
// a row is actually selected.
const VersionComparisonView = lazy(() =>
  import('@localagent/ui/components/viewers/VersionComparisonView').then((m) => ({
    default: m.VersionComparisonView,
  }))
);

/**
 * Stated on the surface rather than left to be inferred from an unexplained
 * count.
 */
const SCOPE_NOTICE =
  'Files written with the file tools. A file written by a shell command is not listed.';

const REFUSAL_TEXT: Record<NonNullable<FileComparisonSide['reason']>, string> = {
  too_large: 'This file is too large to compare here.',
  binary: 'This file has no line structure, so there is nothing to compare.',
  path_unavailable: 'This file does not resolve at one of the two versions.',
};

function refusedSide(comparison: FileComparisonResponse): FileComparisonSide | null {
  if (comparison.before.status !== 'ok') return comparison.before;
  if (comparison.after.status !== 'ok') return comparison.after;
  return null;
}

function workspaceFileHref(workspaceId: string, path: string): string {
  return `/workspaces/${workspaceId}?tab=files&path=${encodeURIComponent(path)}`;
}

function CountCell({ file }: { file: WorkItemFile }) {
  if (file.added_lines === null || file.removed_lines === null) {
    return (
      <span className="text-micro text-muted-foreground" data-testid="work-item-file-no-counts">
        no line counts
      </span>
    );
  }
  return (
    <span className="font-mono text-micro">
      <span className="text-success-text">+{file.added_lines}</span>{' '}
      <span className="text-error-text">−{file.removed_lines}</span>
    </span>
  );
}

function ComparisonPane({
  workspaceId,
  path,
  fromVersion,
  toVersion,
  onClose,
}: {
  workspaceId: string;
  path: string;
  fromVersion: string;
  toVersion: string;
  onClose: () => void;
}) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  const {
    data: comparison,
    isLoading,
    error,
  } = useQuery<FileComparisonResponse, Error>({
    queryKey: ['work', 'file-comparison', workspaceId, path, fromVersion, toVersion],
    queryFn: () =>
      getSDK().workManagement.compareFileVersions(workspaceId, {
        path,
        from_version: fromVersion,
        to_version: toVersion,
      }),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
  const comparisonError = errorWithNothingCachedToShow(comparison, error);

  if (isLoading) {
    return <LoadingState variant="inline" label="Loading comparison..." className="mt-1 text-micro" />;
  }
  if (comparisonError) {
    return (
      <p className="mt-1 text-micro text-destructive" data-testid="work-item-file-comparison-error">
        Failed to load the comparison: {comparisonError.message}
      </p>
    );
  }
  if (!comparison) return null;

  // A side that cannot be shown whole is REFUSED with its reason and a route to
  // the file itself — never rendered in part.
  const refused = refusedSide(comparison);
  if (refused) {
    return (
      <div
        className="mt-1 space-y-1 rounded bg-muted p-2"
        data-testid="work-item-file-comparison-unavailable"
        data-reason={refused.reason}
      >
        <p className="text-micro text-muted-foreground">
          {refused.reason ? REFUSAL_TEXT[refused.reason] : 'This file cannot be compared here.'}
        </p>
        <Link
          to={workspaceFileHref(workspaceId, path)}
          className="text-micro font-medium text-primary hover:underline"
          data-testid="work-item-file-open-normally"
        >
          Open the file in the workspace
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-1 h-80" data-testid="work-item-file-comparison">
      <Suspense
        fallback={<LoadingState variant="inline" label="Loading comparison..." className="text-micro" />}
      >
        <VersionComparisonView
          filename={path}
          before={comparison.before.content ?? ''}
          after={comparison.after.content ?? ''}
          beforeLabel="before this work item"
          afterLabel="after"
          onClose={onClose}
        />
      </Suspense>
    </div>
  );
}

export function WorkItemFiles({
  workItemKind,
  workItemId,
}: {
  /** `ticket`, `project` or `goal` — the route's own vocabulary. */
  workItemKind: string;
  workItemId: string;
}) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  const {
    data: listed,
    isLoading,
    error,
  } = useQuery<WorkItemFilesResponse, Error>({
    queryKey: ['work', 'work-item-files', workItemKind, workItemId],
    queryFn: () => getSDK().workManagement.getWorkItemFiles(workItemKind, workItemId),
    enabled: Boolean(isAuthenticated && sdkConfigured && workItemId),
  });
  const listError = errorWithNothingCachedToShow(listed, error);

  const comparable =
    listed?.workspace_id != null && listed.from_version != null && listed.to_version != null;

  return (
    <section data-testid="work-item-files" data-count={listed?.files.length ?? 0}>
      <h4 className="text-micro font-semibold uppercase text-muted-foreground">Changed files</h4>

      {isLoading && <LoadingState variant="inline" label="Loading files..." className="mt-1 text-micro" />}
      {listError && (
        <p className="mt-1 text-micro text-destructive" data-testid="work-item-files-error">
          Failed to load changed files: {listError.message}
        </p>
      )}

      {listed && listed.files.length === 0 && (
        <p className="mt-1 text-micro text-muted-foreground">No files changed yet.</p>
      )}

      {listed && listed.files.length > 0 && (
        <ul className="mt-1 space-y-1">
          {listed.files.map((file) => {
            const selected = selectedPath === file.path;
            return (
              <li key={file.path} data-testid={`work-item-file-${file.path}`}>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={cn(
                      'min-w-0 flex-1 truncate text-left font-mono text-micro hover:underline',
                      selected ? 'text-primary' : 'text-foreground'
                    )}
                    title={
                      comparable
                        ? 'Compare this file with how it stood before'
                        : 'No pair of stored versions brackets these writes yet'
                    }
                    disabled={!comparable}
                    data-testid={`work-item-file-select-${file.path}`}
                    onClick={() => setSelectedPath(selected ? null : file.path)}
                  >
                    {file.path}
                  </button>
                  <CountCell file={file} />
                  <Link
                    to={`/chat/${file.session_id}`}
                    className="text-micro text-muted-foreground hover:text-foreground"
                    title="Open the session that wrote this file"
                    data-testid={`work-item-file-session-${file.path}`}
                  >
                    Session
                  </Link>
                </div>
                <p className="text-micro text-muted-foreground">
                  Last written {new Date(file.last_written_at).toLocaleString()}
                </p>

                {selected && comparable && (
                  <ComparisonPane
                    workspaceId={listed.workspace_id as string}
                    path={file.path}
                    fromVersion={listed.from_version as string}
                    toVersion={listed.to_version as string}
                    onClose={() => setSelectedPath(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {listed && listed.files.length > 0 && !listed.counts_measured && (
        <p className="mt-1 text-micro text-muted-foreground" data-testid="work-item-files-uncounted">
          Line counts are not available yet — no pair of stored versions brackets these writes.
        </p>
      )}
      {listed?.pending_capture && (
        <p className="mt-1 text-micro text-muted-foreground" data-testid="work-item-files-pending">
          Some writes are newer than the last stored version, so the counts do not include them yet.
        </p>
      )}

      {listed && (
        <p className="mt-1 text-micro text-muted-foreground" data-testid="work-item-files-scope">
          {SCOPE_NOTICE}
        </p>
      )}
    </section>
  );
}
